import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useApolloClient, useQuery, skipToken } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { mdiArrowDown } from '@mdi/js';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { AddButton } from 'common/components/Button/AddButton';
import { ReceivedCertificateListItem } from 'common/components/ReceivedCertificateListItem';
import {
    IBasicUserFragment,
    IOfferEventType,
    IRole,
    ISkillInputOptionFragment,
    CurrentUserDocument,
    ReceivedCertificatesDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { BoxLoader } from 'common/components/Loader';
import { CertificateGenerateDialog } from 'common/components/CertificateGenerateDialog';
import { Divider } from 'common/components/Divider';
import { useCanManageUser } from 'user/hooks';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { ReceivedCertificateOfferEvents } from 'common/components/ReceivedCertificateDrawer/ReceivedCertificateOfferEvents';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';
import { TCertificateOption } from 'certificate/types';
import { UserDrawerHeader } from 'user/components/UserDrawerHeader';
import { UserActionBar } from 'user/UserActionBar';

interface IProps extends IPageDrawerProps {
    canGenerateCertificate?: boolean;
    isExternalCertificate?: boolean;
    hideUserBar?: boolean;
    certificateId: string;
    requiresValidation?: boolean;
    title?: string;
    user?: IBasicUserFragment;
    skill?: ISkillInputOptionFragment | null;
    onClose?(): void;
    onDeleteCertificate?(): void;
    onGenerateCertificate?(): void;
    onSetExternalStatus?(): void;
}

const PAGE_SIZE = 5;

export const ReceivedCertificateDrawer = ({
    canGenerateCertificate: canGenerateCertificateProp,
    isExternalCertificate,
    hideUserBar,
    certificateId,
    requiresValidation,
    open,
    title,
    user,
    skill,
    onClose,
    onDeleteCertificate,
    onGenerateCertificate,
    onSetExternalStatus,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const { showApolloError } = useApolloError();
    const { data: currentUserData, loading: loadingCurrentUser } =
        useQuery(CurrentUserDocument);

    const userId = user?.id;
    const currentUser = currentUserData?.currentUser;

    const isCurrentUser = currentUser?.id === userId;

    const [isLoadingOfferEvents, setIsLoadingOfferEvents] = useState({
        0: false,
        1: false,
        2: false,
    });
    const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);

    const isSkillMatrixPage = !!useRouteMatch('SKILLS_MATRIX');

    const currentUserIsManager = currentUser?.roles?.includes(IRole.Manager);

    const skipCanManageUser =
        !user?.id || loadingCurrentUser || currentUserIsManager;
    const { isGroupManager: isGroupManagerOfUser } = useCanManageUser(
        user?.id,
        skipCanManageUser
    );

    const {
        data,
        loading: receivedCertificatesLoading,
        refetch,
        fetchMore,
        networkStatus,
        error: receivedCertificatesError,
    } = useQuery(
        ReceivedCertificatesDocument,
        userId
            ? {
                  variables: { userId, certificateId, first: 1 },
                  fetchPolicy: 'network-only',
                  notifyOnNetworkStatusChange: true,
              }
            : skipToken
    );

    useEffect(() => {
        if (receivedCertificatesError) {
            showApolloError(receivedCertificatesError);
        }
    }, [receivedCertificatesError, showApolloError]);

    const receivedCertificates =
        data?.receivedCertificates?.edges
            .map((edge) => edge?.node)
            .filter(Boolean) || [];

    const refetchQueries = () => {
        refetch();

        client.cache.evict({ fieldName: 'skillPortfolioReport' });
        client.cache.gc();
    };

    const handleGenerateCertificate = () => {
        onGenerateCertificate?.();

        refetchQueries();
    };

    const handleDeleteCertificate = () => {
        onDeleteCertificate?.();

        refetchQueries();
    };

    const handleSetExternalStatus = () => {
        onSetExternalStatus?.();

        refetchQueries();
    };

    const canAddOwnCertificate = isCurrentUser && currentUserIsManager;
    const canAddUserCertificate =
        !isCurrentUser && (isGroupManagerOfUser || currentUserIsManager);
    const canAddNormalCertificate =
        (canAddOwnCertificate || canAddUserCertificate) && isSkillMatrixPage;
    const canAddExternalCertificate =
        (isCurrentUser || canAddUserCertificate) && isExternalCertificate;

    // Current user can generate a certificate when they are a manager
    // When viewing another user's certificates the group managers and managers can generate a certificate
    // When viewing on the skills matrix page or when explicitly enabled by the caller
    const canGenerateCertificate =
        !loadingCurrentUser &&
        (canAddNormalCertificate ||
            canAddExternalCertificate ||
            canGenerateCertificateProp);

    const receivedCertificatesTotalCount =
        data?.receivedCertificates?.count || 0;
    let moreResultsCount =
        !!receivedCertificatesTotalCount &&
        receivedCertificatesTotalCount - receivedCertificates.length > 0
            ? receivedCertificatesTotalCount - receivedCertificates.length
            : 0;

    // We always show at most PAGE_SIZE more results
    if (moreResultsCount > PAGE_SIZE) moreResultsCount = PAGE_SIZE;

    const initialLoading =
        receivedCertificatesLoading && networkStatus === NetworkStatus.loading;

    const offerEventsAreLoading =
        Object.values(isLoadingOfferEvents).some(Boolean);

    const certificateGenerateDialogTextPrefix =
        'receivedCertificateDrawer.certificateGenerateDialog';
    let certificateGenerateDialogText = `${certificateGenerateDialogTextPrefix}.text`;

    if (isExternalCertificate) {
        certificateGenerateDialogText = requiresValidation
            ? `${certificateGenerateDialogTextPrefix}.externalCertificateValidationText`
            : `${certificateGenerateDialogTextPrefix}.externalCertificateText`;
    }

    const certificateOption: TCertificateOption = {
        id: certificateId,
        name: title ?? '',
    };

    return (
        <PageDrawer open={open} onClose={onClose}>
            {user && !hideUserBar && (
                <>
                    <UserDrawerHeader user={user} />
                    <UserActionBar
                        hideChat
                        hidePortfolio
                        offerEventOptions={{
                            certificate: certificateOption,
                            skill,
                        }}
                        userId={user.id}
                    />
                </>
            )}

            <Box sx={{ p: 4 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h3">{title}</Typography>
                </Box>

                {canGenerateCertificate && (
                    <Box sx={{ mb: 2 }}>
                        <AddButton
                            disabled={receivedCertificatesLoading}
                            onClick={() => setCertificateDialogOpen(true)}
                        >
                            {translate(
                                `receivedCertificateDrawer.${isExternalCertificate ? 'addButtonExternal' : 'addButton'}`
                            )}
                        </AddButton>
                    </Box>
                )}

                <Divider sx={{ mb: 1 }} />

                <Box>
                    {!receivedCertificatesLoading &&
                        !receivedCertificates.length && (
                            <Typography sx={{ mt: 2 }}>
                                {translate(
                                    `receivedCertificateDrawer.${isCurrentUser ? 'noReceivedCertificatesCurrentUser' : 'noReceivedCertificates'}`
                                )}
                            </Typography>
                        )}

                    {!initialLoading &&
                        !!userId &&
                        receivedCertificates.map((receivedCertificate) => (
                            <ReceivedCertificateListItem
                                key={receivedCertificate.id}
                                receivedCertificate={receivedCertificate}
                                userId={userId}
                                onDelete={handleDeleteCertificate}
                                onSetExternalStatus={handleSetExternalStatus}
                            />
                        ))}

                    {initialLoading && <BoxLoader />}

                    {!!moreResultsCount && !initialLoading && (
                        <Button
                            loading={receivedCertificatesLoading}
                            size="small"
                            startIcon={
                                <Icon path={mdiArrowDown} size="1.9rem" />
                            }
                            variant="outlined"
                            onClick={() => {
                                fetchMore({
                                    variables: {
                                        first: PAGE_SIZE,
                                        after: data?.receivedCertificates
                                            ?.pageInfo?.endCursor,
                                    },
                                });
                            }}
                        >
                            {translate(
                                'receivedCertificateDrawer.moreResultsButton',
                                {
                                    count: moreResultsCount,
                                }
                            )}
                        </Button>
                    )}
                </Box>

                {offerEventsAreLoading && <BoxLoader />}

                <ReceivedCertificateOfferEvents
                    certificateId={certificateId}
                    isCurrentUserCertificate={isCurrentUser}
                    type={IOfferEventType.Meeting}
                    onLoading={(isLoading) =>
                        setIsLoadingOfferEvents((prevState) => ({
                            ...prevState,
                            0: isLoading,
                        }))
                    }
                />

                <ReceivedCertificateOfferEvents
                    certificateId={certificateId}
                    isCurrentUserCertificate={isCurrentUser}
                    type={IOfferEventType.Training}
                    onLoading={(isLoading) =>
                        setIsLoadingOfferEvents((prevState) => ({
                            ...prevState,
                            1: isLoading,
                        }))
                    }
                />

                <ReceivedCertificateOfferEvents
                    certificateId={certificateId}
                    isCurrentUserCertificate={isCurrentUser}
                    type={IOfferEventType.Course}
                    onLoading={(isLoading) =>
                        setIsLoadingOfferEvents((prevState) => ({
                            ...prevState,
                            2: isLoading,
                        }))
                    }
                />
            </Box>
            {!!userId && canGenerateCertificate && (
                <CertificateGenerateDialog
                    certificateId={certificateId}
                    confirmText={translate(
                        `receivedCertificateDrawer.certificateGenerateDialog.${isExternalCertificate ? 'externalCertificateConfirmText' : 'confirmText'}`
                    )}
                    isExternalCertificate={isExternalCertificate}
                    open={certificateDialogOpen}
                    text={translate(certificateGenerateDialogText, {
                        userName: user?.name,
                    })}
                    title={translate(
                        `receivedCertificateDrawer.certificateGenerateDialog.${isExternalCertificate ? 'externalCertificateTitle' : 'title'}`
                    )}
                    userIds={[userId]}
                    onClose={() => {
                        setCertificateDialogOpen(false);
                    }}
                    onGenerateCertificate={handleGenerateCertificate}
                />
            )}
        </PageDrawer>
    );
};
